import * as React from "react";
import {
  ApolloError,
  gql,
  useMutation,
} from "@apollo/client";
import {
    DeletePartnerExchangeRateMutationMutation,
    DeletePartnerExchangeRateMutationMutationVariables
} from "../../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../../utils/normalizeErrors";

export const deletePartnerExchangeRateMutation = gql`
  mutation DeletePartnerExchangeRateMutation(
    $id: String!
  ) {
    deletePartnerExchangeRate(
      id: $id
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: DeletePartnerExchangeRateMutationMutationVariables
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const DeletePartnerExchangeRateController: React.FC<Props> = ({
  children,
  onMessage,
}) => {
  const [deletePartnerExchangeRateMutationFn, { loading, error }] =
    useMutation<
      DeletePartnerExchangeRateMutationMutation,
      DeletePartnerExchangeRateMutationMutationVariables
    >(deletePartnerExchangeRateMutation, {
      // Drop the deleted rate from the list if it's on screen.
      refetchQueries: ["FindAllPartnerExchangeRatesQuery"],
    });

  const submit = async (
    values: DeletePartnerExchangeRateMutationMutationVariables
  ) => {
    const id = typeof values.id === "string" ? values.id.trim() : "";
    if (!id) {
      return {
        errors: normalizeErrors([
          { path: "id", message: "Partner exchange rate id is required" },
        ]),
        message: undefined,
      };
    }

    const response = await deletePartnerExchangeRateMutationFn({
      variables: { ...values, id },
    });

    const data = response.data?.deletePartnerExchangeRate;

    const errors = data?.errors
      ? normalizeErrors(data.errors)
      : null;

    if (data?.message && onMessage) {
      onMessage(data.message);
    }

    return {
      errors,
      message: data?.message ?? undefined,
    };
  };

  return children({ submit, loading, error });
};
